import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View, Platform, Pressable } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import useLazyInView from '@/hooks/useLazyInView';
import {
  CATALOG_COVER_ASPECT,
  CATALOG_COVER_HEIGHT,
  CATALOG_COVER_WIDTH,
} from '@/constants/catalogMedia';

// Catalog thumbnails/posters are generated at 1536×1024 (see
// generate_prompt_thumbnails.js) — a true 3:2 ratio. Match it so the cover
// isn't letterboxed/over-cropped.
const ASPECT = CATALOG_COVER_ASPECT;
const isWeb = Platform.OS === 'web';
const isMobileNative = Platform.OS === 'ios' || Platform.OS === 'android';

function WebDetailVideo({ uri }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.play().catch(() => {});
  }, [uri]);

  return (
    <video
      ref={ref}
      src={uri}
      loop
      muted
      playsInline
      autoPlay
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }}
    />
  );
}

let NativeAutoplayVideo;
let MobileInlineVideo;
if (!isWeb) {
  const { useVideoPlayer, VideoView } = require('expo-video');
  if (!isMobileNative) {
    NativeAutoplayVideo = React.memo(({ uri }) => {
      const player = useVideoPlayer(uri, (p) => {
        p.loop = true;
        p.muted = true;
        p.play();
      });
      return (
        <VideoView
          player={player}
          style={StyleSheet.absoluteFillObject}
          nativeControls={false}
          allowsFullscreen={false}
          allowsPictureInPicture={false}
          contentFit="cover"
        />
      );
    });
  }
  MobileInlineVideo = React.memo(({ uri }) => {
    const player = useVideoPlayer(uri, (p) => {
      p.loop = true;
      p.muted = false;
      p.play();
    });
    return (
      <VideoView
        player={player}
        style={StyleSheet.absoluteFillObject}
        nativeControls
        allowsFullscreen
        allowsPictureInPicture={false}
        contentFit="cover"
      />
    );
  });
}

function PromptExampleMedia({ prompt }) {
  const { colors } = useTheme();
  const [nativePlaying, setNativePlaying] = useState(false);
  // Defer loading the (large) cover until it's near the viewport on web.
  const { ref: lazyRef, inView } = useLazyInView();

  const poster = prompt?.exampleThumbnailUrl || prompt?.exampleMediaUrl || '';
  const imageUri = prompt?.exampleMediaUrl || prompt?.exampleThumbnailUrl || '';
  const isVideo = prompt?.outputType === 'video';
  const videoUrl = prompt?.exampleVideoUrl || '';
  const hasMedia = !!(poster || imageUri || videoUrl);
  // LCP candidate on prompt detail pages — load immediately on web (SSR preloads in <head>).
  const shouldLoadMedia = isWeb || inView;

  const showWebVideo = isWeb && isVideo && !!videoUrl;
  const showNativeAutoplay = !isWeb && !isMobileNative && isVideo && !!videoUrl;
  const showMobileVideo = isMobileNative && isVideo && !!videoUrl && nativePlaying;
  const showPlayOverlay = isVideo && !!videoUrl && !showWebVideo && !showNativeAutoplay && !showMobileVideo;

  const styles = useMemo(() => StyleSheet.create({
    wrap: {
      width: '100%',
      borderRadius: 14,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
    },
    media: {
      width: '100%',
      aspectRatio: ASPECT,
      backgroundColor: colors.solid_color_tertiary,
      position: 'relative',
      ...(isWeb ? { minHeight: 1 } : null),
    },
    image: {
      ...StyleSheet.absoluteFillObject,
    },
    badgeRow: {
      position: 'absolute',
      top: 10,
      left: 10,
      flexDirection: 'row',
      gap: 6,
      zIndex: 2,
    },
    badge: {
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 999,
      backgroundColor: 'rgba(0,0,0,0.55)',
    },
    badgeText: {
      color: '#fff',
      fontSize: 11,
      fontWeight: '600',
      textTransform: 'capitalize',
    },
    playOverlay: {
      ...StyleSheet.absoluteFillObject,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.12)',
      zIndex: 1,
    },
    playCircle: {
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: 'rgba(0,0,0,0.55)',
      alignItems: 'center',
      justifyContent: 'center',
    },
  }), [colors]);

  if (!hasMedia) return null;

  const displayUri = (showWebVideo || showNativeAutoplay || showMobileVideo) && poster
    ? poster
    : imageUri;

  return (
    <View
      ref={lazyRef}
      style={styles.wrap}
      accessibilityRole="image"
      accessibilityLabel={prompt?.name ? `Example output for ${prompt.name}` : 'Example output'}
    >
      <View style={styles.media}>
        {displayUri && shouldLoadMedia ? (
          <Image
            source={{ uri: displayUri, width: CATALOG_COVER_WIDTH, height: CATALOG_COVER_HEIGHT }}
            style={styles.image}
            contentFit="cover"
            priority="high"
            placeholder={prompt?.exampleBlurhash ? { blurhash: prompt.exampleBlurhash } : undefined}
            transition={isWeb ? 0 : 200}
            recyclingKey={prompt?.id || prompt?.slug}
            accessibilityLabel={prompt?.name ? `Example output for ${prompt.name}` : 'Example output'}
          />
        ) : (
          <View style={[styles.image, { alignItems: 'center', justifyContent: 'center' }]}>
            <Icon name={isVideo ? 'video' : 'image'} size={36} color={colors.text_color_tertiary} />
          </View>
        )}
        {showWebVideo && shouldLoadMedia ? <WebDetailVideo uri={videoUrl} /> : null}
        {showNativeAutoplay && NativeAutoplayVideo ? <NativeAutoplayVideo uri={videoUrl} /> : null}
        {showMobileVideo && MobileInlineVideo ? <MobileInlineVideo uri={videoUrl} /> : null}
        <View style={styles.badgeRow} pointerEvents="none">
          {prompt?.outputType ? (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>{prompt.outputType}</Text>
            </View>
          ) : null}
          {prompt?.inputType === 'image' ? (
            <View style={styles.badge}>
              <Icon name="upload" size={10} color="#fff" solid />
            </View>
          ) : null}
        </View>
        {showPlayOverlay ? (
          <Pressable
            style={styles.playOverlay}
            onPress={() => setNativePlaying(true)}
            accessibilityRole="button"
            accessibilityLabel="Play example video"
          >
            <View style={styles.playCircle}>
              <Icon name="play" size={20} color="#fff" solid />
            </View>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

export default React.memo(PromptExampleMedia);
